import { Camera } from '@capacitor/camera';
import { ZenputFile, ZenputFiles } from '../../../shared/models/files';
import { ModalOptionsView } from '../../../shared/views/ModalOptionsView';
import { BaseFieldView } from '../BaseField/views';
import { MobileFormStructure } from '../MobileFormStructure/models';
import { createUUID } from '../../../../backbone-unorganized/utils/files';
import { CordovaPlugins } from '../../../../shared/CordovaPlugins';
import { isAndroid, isWeb, isWindows } from '../../../../shared/utils/is';
import { actionSheet, showErrorMessage, showPermissionDeniedMessage } from '../../../../shared/utils/notifications';
import { ImageVideoPickerDialog } from '../../../../react/shared/ImageVideoPickerDialog';
import { ReactPageWrapper } from '../../../shared/views/ReactPageWrapper';
import { MIME_TYPE_TO_EXTENSION } from '../../../../constants';
import { Webcam } from '@zenput/feature-zencam';

export const ImageAndVideoFieldViewBase = BaseFieldView.extend({
    hide_caption: false,
    required_permissions: '',

    events: {
        'change .notes_field': 'save_notes',
        'keyup textarea': 'expand_textarea',
        'click .take_photo': 'click_take_photo_video',
        'click .take_video': 'click_take_photo_video'
    },

    templateHelpers: function () {
        var super_helpers = ImageAndVideoFieldViewBase.__super__.templateHelpers.apply(this, arguments);

        return $.extend(super_helpers, {
            hide_caption: this.hide_caption
        });
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.model.has_value() && this.model.get('hidden')) {
            this.$el.hide();
        }

        ImageAndVideoFieldViewBase.__super__.initialize.call(this, options);
    },

    collect_media_camera: function () {
        throw new Error('Programmer Error: collect_media_camera() not implemented');
    },

    collect_media_gallery: function () {
        throw new Error('Programmer Error: collect_media_gallery() not implemented');
    },

    collect_media_web: function () {
        throw new Error('Programmer Error: collect_media_web() not implemented');
    },

    openImageEditor: function () {
        throw new Error('Programmer Error: openImageEditor() not implemented');
    },

    addMediaFileToStorage: function (blob, imageQualityStats = undefined) {
        const extensionFile = MIME_TYPE_TO_EXTENSION[blob.type];
        const uniqueFileId = createUUID();

        const mediaFile = new ZenputFile({
            id: uniqueFileId,
            raw_file: new File([blob], `${uniqueFileId}.${extensionFile}`, { type: blob.type })
        });

        $f.mobile.uploads.web_filestore.set_file(mediaFile);
        this.on_collect_media_success(new ZenputFiles([mediaFile]), imageQualityStats);
    },

    handleCameraPermissions: function () {
        const cameraPermission = navigator.permissions.query({ name: 'camera' });
        const microphonePermission = navigator.permissions.query({ name: 'microphone' });
        const isVideoField = this.model.get('type') === 'video';

        let deniedMessage = _t('mobile_message_media_perimission_denied') + _t('mobile_common_camera');

        if (isVideoField) {
            deniedMessage += _t('mobile_field_temperature_operators_separator') + _t('mobile_common_microphone');
        }

        const tryToAccessMediaDevices = async () => {
            try {
                const _mediaTrack = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: isVideoField
                });
                // Stop connection
                _mediaTrack.getTracks().forEach((track) => track.stop());

                this.collectMediaWebCamera();
            } catch {
                // Access was denied or browser doesn't have access
                showPermissionDeniedMessage(deniedMessage);
            }
        };
        if (cameraPermission.state === 'denied' || (isVideoField ? microphonePermission.state === 'denied' : false)) {
            showPermissionDeniedMessage(deniedMessage);
        } else if (
            cameraPermission.state === 'prompt' ||
            (isVideoField ? microphonePermission.state === 'prompt' : false)
        ) {
            tryToAccessMediaDevices();
        } else {
            tryToAccessMediaDevices();
        }
    },

    openFilePickerDialog: function () {
        const handleOnSelect = async (option) => {
            if (!option) return;

            if (option === 'browse') {
                this.collect_media_web();
                return;
            }

            if (option === 'camera') {
                this.handleCameraPermissions();
            }
        };

        const title =
            this.model.get('type') === 'image'
                ? _t('mobile_field_image_picker_dialog_title')
                : _t('mobile_field_video_picker_dialog_title');

        const translatedStrings = {
            cameraLabel: _t('mobile_common_camera'),
            galleryLabel: _t('mobile_action_sheet_media_gallery'),
            cancelLabel: _t('mobile_action_sheet_cancel')
        };

        const elem = this.$el.find('div.source_selector')[0];

        window.mountReactJSX(elem, () => (
            <ImageVideoPickerDialog title={title} onSelect={handleOnSelect} translatedStrings={translatedStrings} />
        ));
    },

    onClose: function () {
        const elem = this.$el.find('div.source_selector')[0];
        window.unmountReact(elem);
    },

    collectMediaWebCamera: function () {
        const cameraMode = this.model.get('type') === 'image' ? 'photo' : 'video';
        const _this = this;

        $f.mobile.nav.show_modal(ReactPageWrapper, {
            renderFunction: function () {
                return (
                    <Webcam
                        mode={cameraMode}
                        onCapture={(blob) => {
                            if (cameraMode === 'video') {
                                _this.addMediaFileToStorage(blob);
                            }

                            if (cameraMode === 'photo') {
                                _this.openImageEditor(blob);
                            }
                            this.close();
                        }}
                        onGoBack={() => {
                            this.close();
                        }}
                    />
                );
            }
        });
    },

    on_select: function () {
        // optional callback for callers
    },

    _is_force_camera: function () {
        if (!this.model.collection || !(this.model.collection instanceof MobileFormStructure)) {
            return false; // tasks don't have collections
        }

        return this.model.collection.metadata.get('is_force_camera');
    },

    click_take_photo_video: function (event) {
        var this_ = this;

        // We need to make sure the user hasn't pressed cancel yet. Mark form updated before we
        // leave to get an image, as phonegap will call on_resume() before we get a chance to set
        // our model and prevent a form reload.
        if (this.model.collection) {
            this.model.collection.smetadata.set('date_modified', { $date: new Date().getTime() });
        }

        if (isWeb()) {
            // If the form is set to prevent gallery selection, open camera directly otherwise prompt user for file source
            if (this._is_force_camera()) {
                this.handleCameraPermissions();
            } else {
                this.openFilePickerDialog();
            }
            return false;
        }

        this._native_action_sheet_use_gallery(function (use_gallery) {
            if (use_gallery) {
                this_.collect_media_gallery();
            } else {
                this_.collect_media_camera();
            }
        });

        return false;
    },
    /**
     * ActionSHeet (usually by asking the user) if we should use the gallery or the camera.
     * @param  {Function} callback - Passes true for gallery and false for camera
     */
    _native_action_sheet_use_gallery: function (callback) {
        if (this._is_force_camera()) {
            return callback(false);
        }

        var button_rect = this.$el.find('.add_media_btn').get(0).getBoundingClientRect();

        var action_sheet_callback = function (button_idx) {
            if (button_idx === 3) {
                return;
            } // cancel
            callback(button_idx === 2); // true for gallery, false for camera.
        };

        if (isWindows()) {
            $f.application.content_overlay.show(
                new ModalOptionsView({
                    top: button_rect.top + this.$el.find('.add_media_btn').outerHeight() + 5,
                    right: $f.window.viewport.width - button_rect.right,
                    buttons: [_t('mobile_common_camera'), _t('mobile_action_sheet_media_gallery')],
                    callback: action_sheet_callback
                })
            );
            return;
        }

        actionSheet(
            {
                position: [button_rect.left - 20, button_rect.top + 20], // only used for iPad
                androidTheme: CordovaPlugins().actionsheet.ANDROID_THEMES.THEME_DEVICE_DEFAULT_LIGHT,
                buttonLabels: [_t('mobile_common_camera'), _t('mobile_action_sheet_media_gallery')],
                androidEnableCancelButton: true,
                addCancelButtonWithLabel: _t('mobile_action_sheet_cancel')
            },
            action_sheet_callback
        );
    },

    /**
     * Convert a list of file paths or web File objects to a ZenputFiles collection.
     *
     * @param  {array[string|File]}          arr    An array of Files or paths (depending on web vs native)
     * @return {ZenputFiles}        The resulting collection
     */
    _to_file_collection: function (arr) {
        var this_ = this;

        return new ZenputFiles(
            _.map(arr, function (path_or_file, i) {
                if (!path_or_file) {
                    return null;
                }

                var is_remote_image = _.contains(this_.model.get('value') || [], path_or_file);

                if (!is_remote_image && typeof path_or_file === 'string' && path_or_file.indexOf('://') === -1) {
                    path_or_file = 'file://' + path_or_file;
                }

                var model_dict = {
                    id: createUUID(i),

                    // @author:david we should really get this from cordova
                    mimetype: this_.model.get('type') === 'image' ? 'image/jpg' : 'video/mp4'
                };

                if (is_remote_image) {
                    model_dict.url_s3 = path_or_file;
                } else if (typeof path_or_file === 'string') {
                    model_dict.url_native = path_or_file;
                } else {
                    model_dict.raw_file = path_or_file;
                }

                return model_dict;
            })
        );
    },

    /**
     * Provided a set of files from native code, pre-process them and send them to the field model for saving.
     *
     * @param  {ZenputFiles} files The file objects to save
     */
    on_collect_media_success: function (files, imageQualityStats = undefined) {
        var this_ = this;

        if (!files || !files.length) {
            return; // user cancelled
        }

        if ($f.flags.get_flag('detect_low_quality_photos')) {
            if (this_.model.get('type') === 'image') {
                files.forEach((file) => {
                    file.set(imageQualityStats);
                });
            }
        }

        // innocent until proven guilty
        this.$el.removeClass('invalid');
        this.model.is_valid = true;
        this.trigger('request_error_wizard_recalculation', this.model);

        this.model.save_files(
            {
                add: files
            },
            function () {
                this_.on_select();
            }
        );
    },

    on_collect_media_error: function (error, selected_source_type) {
        // Since multiple plugins call this with multiple error types,
        // let's create an error object that has a consistent structure: { code: #, msg: '...' }
        if (typeof error !== 'object') {
            error = {
                code: typeof error === 'number' ? error : null,
                message: typeof error === 'string' ? error : null
            };
        }

        if (
            _.contains(
                ['camera cancelled.', 'selection cancelled.', 'no image selected', "user didn't choose a file."],
                (error.message || '').toLowerCase()
            )
        ) {
            return; // user cancelled
        }

        if (error.code === 3 || error.code === 5) {
            return; // user cancelled
        }

        if (
            _.contains([20, 4], error.code) ||
            (error.message &&
                (error.message.indexOf('has no access to') > -1 || error.message.indexOf('Permission Denial') > -1))
        ) {
            showPermissionDeniedMessage(
                _t('mobile_message_media_perimission_denied') +
                    (selected_source_type === 'library'
                        ? _t('mobile_message_media_perimission_denied_device')
                        : _t('mobile_common_camera'))
            );
            return;
        }

        if (isAndroid() && error.code === 30) {
            showErrorMessage(error.message);
            return;
        }

        // Default to vague error message
        showErrorMessage();
        $f.mobile.logging.log_error('couldnt get media from device', error);
    },

    is_gallery_permission_granted: async function (callback) {
        callback = callback || function () {};

        if (isWindows()) {
            return callback(true);
        }

        if (this.required_permissions === 'photos') {
            return callback(true);
        }

        const requestedPermissions = await Camera.requestPermissions({ permissions: ['camera'] });
        const hasRequestedPermissions = this.has_camera_and_media_permissions(requestedPermissions);
        return callback(hasRequestedPermissions);
    },

    has_camera_and_media_permissions: function (requestedPermissions) {
        const { camera, photos } = requestedPermissions;
        const cameraGranted = camera === 'granted';
        const photosGranted = ['granted', 'limited'].includes(photos);
        return cameraGranted && photosGranted;
    },
    save_notes: function (event) {
        this.model.set({ notes: $(event.target).val() }, { validate: false });
    }
});
